import { Dispatch } from 'redux';
import { getViewportWidth, WidthPresets } from '../common/helpers/viewportHelpers';
import style from './sharing.module.scss';
import classNames from 'classnames';
import { AppState, SharingType } from 'acscp/store';
import { defineMessages, FormattedMessage } from 'react-intl';
import React from 'react';
import { PresentationComponentBase } from '../presentationComponentBase';
import { AddNotification, FetchRequest } from 'acscp/actions';
import { UnshareRequest } from 'acscp/api/requests/unshareRequest';
import { RequestClass, ShareRequest } from 'acscp/api/requests';
import { NotificationType } from 'acscp/store/notificationState';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { RequestStateProps } from '../common/helpers/requestStateProps';
import { LocaleCode } from 'acscp/translations';

interface UnshareReshareOwnProps {
    showPopup: boolean;
    bookingId: string;
    email: string;
    sharingTypes: SharingType[];
    closer: () => void;
    sourceRef: HTMLDivElement | null;
}

interface UnshareReshareStateProps {
    locale: LocaleCode;
}

interface UnshareReshareDispatchProps {
    share: (bookingId: string, emails: string[], sharingTypes: SharingType[], locale: LocaleCode) => void;
    unshare: (bookingId: string, email: string) => void;
}

export class UnshareResharePopupPresentational
    extends PresentationComponentBase<UnshareReshareOwnProps, UnshareReshareStateProps, UnshareReshareDispatchProps, {}> {
    constructor(props: UnshareReshareOwnProps & UnshareReshareStateProps & UnshareReshareDispatchProps) {
        super(props);
    }

    private popupElement: HTMLDivElement | null;

    private startY: number;
    private startTime: number;

    componentWillMount(): void {
        window.addEventListener('resize', this.setLocation);
    }

    componentDidUpdate(): void {
        this.setClickListener();
        this.setLocation();
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.setLocation);
    }

    private setLocation = () => {
        if (!!this.popupElement && !!this.props.sourceRef) {
            if (getViewportWidth() >= WidthPresets.tabletMin) {
                const ourLocation = this.popupElement.getBoundingClientRect();
                const lloc = this.props.sourceRef.offsetLeft;
                const rloc = lloc + this.props.sourceRef.offsetWidth;
                const mloc = this.props.sourceRef.offsetTop + this.props.sourceRef.offsetHeight / 2;
                this.popupElement.style.bottom = 'auto';
                this.popupElement.style.left = (rloc + 10).toString() + 'px';
                this.popupElement.style.top = (mloc - ourLocation.height / 2 ).toString() + 'px';
                // if the popup goes too far off the right of the screen, we reposition it to the left instead
                const newLocation = this.popupElement.getBoundingClientRect();
                if (newLocation.left + newLocation.width > getViewportWidth()) {
                    this.popupElement.style.left = (lloc - newLocation.width).toString() + 'px';
                }
            } else {
                this.popupElement.style.top = 'auto';
                this.popupElement.style.left = '0px';
                this.popupElement.style.bottom = '0px';
            }
        }
    }

    private setClickListener = () => {
        if (this.props.showPopup) {
            document.addEventListener('click', this.closeIfClickOutside);
            document.addEventListener('touchstart', this.logTouchStart);
            document.addEventListener('touchend', this.closeIfSwipeDown);
        } else {
            document.removeEventListener('click', this.closeIfClickOutside);
            document.removeEventListener('touchstart', this.logTouchStart);
            document.removeEventListener('touchend', this.closeIfSwipeDown);
        }
    }

    private internalUnshare = () => {
        this.props.unshare(this.props.bookingId, this.props.email);
        this.props.closer();
    }

    private internalReshare = () => {
        this.props.share(this.props.bookingId, [this.props.email], this.props.sharingTypes, this.props.locale);
        this.props.closer();
    }

    private closeIfClickOutside = (event: Event) => {
        const target = event.target as Node;
        if (!!this.popupElement && !this.popupElement.contains(target)) {
            this.props.closer();
        }
    }

    private logTouchStart = (event: TouchEvent) => {
        this.startY = event.changedTouches[0].screenY;
        this.startTime = event.timeStamp;
    }

    private closeIfSwipeDown = (event: TouchEvent) => {
        const movement = event.changedTouches[0].screenY - this.startY;
        const duration = event.timeStamp - this.startTime;
        if (movement > 100 && duration > 100 && duration < 1000) {
            this.props.closer();
        }
    }

    render(): JSX.Element {
        return (
            <div
                className={classNames(style.SharingPopup, {[style.HidePopup]: !this.props.showPopup})}
                ref={node => this.popupElement = node}
            >
                <div className={style.PopupButton} onClick={this.internalUnshare}>
                    <div className={style.PopupButtonText}>
                        <FormattedMessage
                            id={'components.sharingpage.popup.unshare'}
                            defaultMessage={'UNSHARE'}
                        />
                    </div>
                </div>
                <div className={style.PopupButton} onClick={this.internalReshare}>
                    <div className={style.PopupButtonText}>
                        <FormattedMessage
                            id={'components.sharingpage.popup.reshare'}
                            defaultMessage={'RESHARE'}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: UnshareReshareOwnProps, getRequestError: (request: RequestClass<any>) => RequestStateProps
): UnshareReshareStateProps {
    const locale = state.intl.locale;
    return { locale };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: UnshareReshareOwnProps): UnshareReshareDispatchProps {
    return {
        unshare: (bookingId: string, email: string) => dispatch(new FetchRequest(new UnshareRequest(bookingId, email))),
        share: (bookingId: string, emails: string[], sharingTypes: SharingType[], locale: LocaleCode) =>
            dispatch(new FetchRequest(new ShareRequest(bookingId, emails, sharingTypes, locale)))
    };
}

export const UnshareResharePopup = connectWithRequests(mapStateToProps, mapDispatchToProps)(UnshareResharePopupPresentational);
